import { useEffect, useState } from 'react';
import { authenticatedFetch } from './authenticated-fetch';
import type { PaletteHit } from './palette-recents';
import type { SearchSourceKey } from '../components/palette-sections';

/**
 * Server hits carry no `perm` — the caller stamps one on per source, since
 * which permission gated a hit is knowledge of the group it arrived in, not of
 * the row. Typing these as full `PaletteHit`s would claim a field the wire
 * never sends.
 */
export type AdminSearchHit = Omit<PaletteHit, 'perm'>;

export type AdminSearchResults = Partial<Record<SearchSourceKey, AdminSearchHit[]>>;

const DEBOUNCE_MS = 200;

/**
 * Debounced search against GET /api/search. An in-flight request is aborted
 * when the query changes, so a slow response can never overwrite a newer one.
 */
export function useAdminSearch(query: string): {
  results: AdminSearchResults;
  isLoading: boolean;
  error: string | null;
} {
  const [results, setResults] = useState<AdminSearchResults>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults({});
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = await authenticatedFetch(`/api/search?q=${encodeURIComponent(trimmed)}`, {
            signal: controller.signal,
          });
          if (!res.ok) {
            setResults({});
            setError('Search failed');
            setIsLoading(false);
            return;
          }
          const body = (await res.json()) as { data: AdminSearchResults };
          setResults(body.data ?? {});
          setError(null);
          setIsLoading(false);
        } catch (err) {
          // An aborted request means a newer query superseded this one — leave
          // the last good results in place rather than flashing an error for
          // something the user didn't cause. Crucially, do NOT touch isLoading
          // here either: the superseding request is still in flight (or still
          // debouncing), and clearing it would render this stale settle as if
          // it answered the new query.
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (controller.signal.aborted) return;
          // A genuine failure (DNS, CORS, 5xx already handled above, timeout,
          // etc.) must not display stale results as if they answered this
          // query — clear them and surface the error instead.
          setResults({});
          setError('Search failed');
          setIsLoading(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return { results, isLoading, error };
}
